import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { InputTextModule } from 'primeng/inputtext';
import { FormsModule } from '@angular/forms';
import { CommonModule, DecimalPipe } from '@angular/common';
import { TranslocoPipe } from '@jsverse/transloco';
import { MiniCalculatorComponent } from "../mini-calculator/mini-calculator.component";

@Component({
  selector: 'app-custom-number-input',
  standalone: true,
  imports: [FormsModule, InputTextModule, CommonModule, TranslocoPipe, MiniCalculatorComponent],
  providers: [DecimalPipe],
  templateUrl: './custom-number-input.component.html',
  styleUrl: './custom-number-input.component.css'
})
export class CustomNumberInputComponent {
  @Input() labelText: string = '';
  @Input() iconSrc: string | null = null;
  @Input() iconAltText: string = '...';
  @Input() inputId: string = 'input';
  @Input() inputType: string = 'text';
  @Input() placeholder: string = '';
  @Input() errorMessage: string | null = null;
  @Input() warningMessage: string | null = null;
  @Input() isDisabled: boolean = false;
  @Input() boxHeight: string = '';
  @Input() bgColor: string = '';

  @Input() inputValue: string | undefined;
  @Output() inputValueChange = new EventEmitter<string>();
  @Output() inputValueLength = new EventEmitter<number>();
  @Input() iconLeftOffset: number = 10;
  @Input() dividerOffset: number = 8;
  @Input() textInputOffset: number = 8;
  @Input() iconWidth: number = 20;
  @Input() iconHeight: number = 20;
  @Input() autocomplete: string = 'off';
  @Input() autoMarginTop: string = 'mt-4';

  @Input() title: string = '';
  @Input() disabledTitle: string | null = null;
  @Input() showOnlyValueWhenDisabled: boolean = false;
  @Input() showCustomDecimals: string = '1.2-2';

  @Input() allowCalculator: boolean = false;
  @Output() trackCalcExprHistoryChange = new EventEmitter<string | null>();
  trackCalcExprHistory: string | null = null;

  showTextType: string = 'text';

  rawInput = '';
  lastValidInput: string = '';

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['inputType'] && changes['inputType'].currentValue) {
      this.showTextType = 'text';
    }

    if (changes['inputValue'] && changes['inputValue'].currentValue != null) {
      const newVal: number = changes['inputValue'].currentValue;

      const currentParsed = this.parseToRawNumber(this.rawInput);
      if (currentParsed != newVal) {
        // Convert number to string with comma as decimal
        const [intPart, decimalPart] = newVal.toString().split('.');

        const localized = decimalPart !== undefined
          ? `${intPart},${decimalPart}`
          : intPart;

        this.rawInput = this.formatForDisplay(localized);
        this.lastValidInput = this.rawInput;
      }
    }
  }

  onInputChange(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const input = inputEl.value;

    // Allow only digits, commas, dots
    let filtered = input.replace(/[^0-9.,]/g, '');

    // Allow only one comma
    const commaCount = (filtered.match(/,/g) || []).length;
    if (commaCount > 1) {
      // Revert to last valid input
      inputEl.value = this.lastValidInput;
      return;
    }

    this.rawInput = filtered;
    this.lastValidInput = filtered;

    const parsed = this.parseToRawNumber(this.rawInput);
    this.inputValue = parsed?.toString();
    this.inputValueChange.emit(parsed?.toString());
    this.inputValueLength.emit(input.length);
  }

  onBlurFormat(): void {
    if (this.rawInput) {
      this.rawInput = this.formatForDisplay(this.rawInput);
    }
  }

  private parseToRawNumber(input: string): number | null {
    const normalized = input.replace(/\./g, '').replace(',', '.');
    const parsed = parseFloat(normalized);
    return isNaN(parsed) ? null : parsed;
  }

  private formatForDisplay(input: string): string {
    const [intPartRaw, decimalPart] = input.split(',');

    const cleanInt = intPartRaw.replace(/\./g, '');
    const cleanDecimal = decimalPart ? decimalPart.replace(/\./g, '') : undefined;
    const reversed = cleanInt.split('').reverse();
    const grouped: string[] = [];

    for (let i = 0; i < reversed.length; i++) {
      if (i > 0 && i % 3 === 0) {
        grouped.push('.');
      }
      grouped.push(reversed[i]);
    }

    const formattedInt = grouped.reverse().join('');
    return cleanDecimal !== undefined ? `${formattedInt},${cleanDecimal}` : formattedInt;
  }


  getIconStyles(): { [key: string]: string } {
    return { left: this.iconLeftOffset + 'px' };
  }

  getDividerStyles(): { [key: string]: string } {
    return { left: (this.iconLeftOffset + this.iconWidth + this.dividerOffset) + 'px' };
  }

  getInputPaddingStyles(): { [key: string]: string } {
    if (this.iconSrc) {
      return { paddingLeft: `${this.iconLeftOffset + this.dividerOffset + this.iconWidth + this.textInputOffset}px` };
    } else {
      return { paddingLeft: `${this.textInputOffset}px` };
    }
  }

  getIconSizeStyles(): { [key: string]: string } {
    return {
      width: `${this.iconWidth}px`,
      height: `${this.iconHeight}px`,
    };
  }

  get getTitle(): string {
    if (this.isDisabled && this.disabledTitle != null) {
      return this.disabledTitle;
    }

    return this.title;
  }

  onCalcAccept(result: number) {
    const localized = this.numberToLocalized(result);
    const displayed = this.formatForDisplay(localized);
    this.onInputChange({ target: { value: displayed } } as unknown as Event);
  }

  private numberToLocalized(n: number): string {
    const [i, d] = String(n).split('.');
    return d !== undefined ? `${i},${d}` : i;
  }

  onChildCalcExprChanged(value: string | null) {
    this.trackCalcExprHistory = value;
    this.trackCalcExprHistoryChange.emit(value);
  }
}
